[
  {
    "version": "1.6.2",
    "date": "2026-11-02",
    "title": "Jev developer JSON viewer",
    "changes": [
      "Admins can toggle is_a_cool_dev for each user. Access defaults off for regular accounts and on for the owner.",
      "Developer accounts get a Jev JSON button showing raw requests, responses, selected shot plans and fallback reasons for their own games."
    ]
  },
  {
    "version": "1.6.1",
    "date": "2026-10-20",
    "title": "Custom input controls, integrated admin access and stricter browser checks",
    "changes": [
      "Top-bar account access now uses a distinct profile treatment with a user icon and account label, aligned with the neighboring buttons on desktop and mobile.",
      "Choose Mouse & Keyboard or Trackpad mode from an in-game menu with matching styling, keyboard navigation and saved preferences.",
      "Owner Admin settings is now an integrated action tile in Account Overview, with a clear description and responsive layout.",
      "Standalone browser tests now mock expected background API requests, eliminating connection-refused noise from the absent test backend.",
      "Unexpected API calls fail with a clear method and endpoint so missing test coverage cannot silently pass. Feature-specific failure mocks and real-backend online/passkey tests remain separate.",
      "Shared mocks also cover extra pages and mobile browser contexts, with regressions for mock overrides and unhandled requests. Browser suites use separate artifact directories so concurrent runs preserve each other's traces."
    ]
  },
  {
    "version": "1.6.0",
    "date": "2026-10-20",
    "title": "Pocket geometry, smarter position play and account controls",
    "changes": [
      "Pocket mouths, shelves and rendered openings now share dimensions based on published pool equipment specifications.",
      "Pocket capture no longer uses artificial attraction or speed-dependent capture circles. Balls crossing above a pocket remain in flight, and grounded entries use the shared pocket geometry.",
      "Pocketed balls fall vertically into the opening, replacing the inward pull and early sinking animation.",
      "CPU and Jev can compare simulated follow-up shots and short endgame runouts within their existing planning budgets. CPU remains entirely offline.",
      "Shot previews and execution share the same cue-impact entry points across local play, online rooms and Jev games, including cue-clearance elevation.",
      "Owner Admin settings now opens from the signed-in account panel: click your username instead of opening Table settings.",
      "Felt and rail color swatches stay circular with larger touch targets on desktop and mobile.",
      "Expanded pocket, cue-stroke, planning and browser regression coverage, including matching Python and browser shot replays; stabilized desktop break-placement checks on CI runners.",
      "Updated backend libraries, frontend build and browser-test tools, and CI actions. Release containers now build with Node 26 and run the backend on Python 3.14.",
      "Refreshed the installed-app cache for this release.",
      "Added research and limitations notes for pocket geometry, cue impact and AI planning. Finite tip contact, new double-hit detection, pocket-liner rebounds and deliberate jump or masse planning remain deferred."
    ]
  },
  {"version": "1.5.7", "date": "2026-10-11", "title": "House rules and ruleset previews", "changes": ["In Bar rules, scratching while on the 8 is an ordinary foul if the 8 stays on the table. Pocketing the 8 with a scratch still loses.", "Compare Bar, Tournament, and your Custom settings before starting a new rack. New visitors can choose Bar or Tournament during welcome."]},
  {"version":"1.5.6","date":"2026-10-11","title":"Varied Jev breaks","changes":["Jev chooses between previewed head-ball and second-ball breaks with varied legal placements, pace, and spin.","Break setups vary between racks while keeping server and client shot physics deterministic.","CPU uses varied offline break plans without Jev requests.","Place and reposition the cue ball behind the head string before breaking. Bar and Tournament allow it; Custom can use a fixed start."]},
  {"version":"1.5.5","date":"2026-10-09","title":"Diagnostics and visit counts","changes":["Show diagnostic references on request errors and explain Jev CPU fallbacks.","Add compressed, bounded application logs with retention across deployments.","Show consented anonymous visit counts in the owner dashboard without browser fingerprinting."]},
  {
    "version": "1.5.4",
    "date": "2026-10-09",
    "title": "Mobile header layout",
    "changes": ["Keep the mobile account button readable when additional game controls are visible.", "Auto camera follows the shot direction for AI and online opponents when the view is misaligned."]
  },
  {
    "version": "1.5.3",
    "date": "2026-10-09",
    "title": "Called-pocket consistency and browser checks",
    "changes": [
      "Select or change a called pocket directly on the table, without a floating overlay. Custom all-ball rules keep a compact ball selector in the shot controls.",
      "Keep tutorial touch controls responsive without rebuilding guidance on every touch.",
      "Hide app installation controls throughout the desktop interface, including touch-capable desktop layouts.",
      "Keep called-pocket indicators tied to the rules at the start of the shot, including the last group ball in bar rules.",
      "Repair remaining browser checks for current account, settings, and new-game flows."
    ]
  },
  {
    "version": "1.5.2",
    "date": "2026-10-08",
    "title": "Browser checks and desktop help",
    "changes": [
      "Give the desktop controls guide more room for readable instructions.",
      "Keep optional turn status consistent with the active player in every game mode.",
      "Run browser, passkey, and multiplayer checks independently with complete diagnostic reports.",
      "Update browser regression checks for current game and input behavior."
    ]
  },
  {
    "version": "1.5.1",
    "date": "2026-10-08",
    "title": "Clearer turns and called pockets",
    "changes": [
      "Clear Jev shot-strategy text when that shot finishes, so it does not linger on the next player\u2019s turn.",
      "Make the selected pocket stand out with a bold gold target, dark outline, and a Called marker.",
      "Cancel a drawn-back shot with a secondary click, then press the primary button again when ready.",
      "Added Easter eggs."
    ]
  },
  {
    "version": "1.5.0",
    "date": "2026-10-08",
    "title": "Easter eggs and account preferences",
    "changes": [
      "Added Easter eggs.",
      "Signed-in appearance, camera input, auto-camera, and playback settings now follow your account across devices. Guest preferences remain local.",
      "Install app controls are shown only in mobile mode.",
      "Fixed grounded object balls passing through cushions after another ball pushed them slightly into the rail.",
      "Preserved full-precision server ball snapshots and aligned replay calculations to prevent predicted pockets being reversed by synchronization."
    ]
  },
  {
    "version": "1.4.0",
    "date": "2026-10-08",
    "title": "Account experience, cue clearance, and smarter shots",
    "changes": [
      "Reorganized account and settings panels with clearer sign-in, profile, and game settings flows.",
      "Focused Google and cancellable passkey prompts, clearer recovery, saved privacy choices, and safer admin budget actions.",
      "Corrected cue clearance near rails and pocket jaws using cushion profiles and a tapered shaft.",
      "Added firm glancing cluster-development options and outcome-based Jev guidance for useful attacking shots.",
      "Raised the default maximum break speed from 8.5 to 9.5 m/s; normal shots and custom speed settings are unchanged.",
      "Winner actions follow the game mode; Restart simulation preserves the selected AI pairing.",
      "Kept opponent power separate from human controls, tightened the idle cue gap, and ignored callbacks from departed online rooms.",
      "Simplified turn status with one player label and capitalized action text, while preserving Jev shot-strategy details.",
      "Updated the Privacy Notice and data inventory for current account, sign-in, usage, and retention behavior.",
      "Corrected Terms to describe monthly Jev allowances; existing users will be asked to accept the updated Terms."
    ]
  },
  {
    "version": "1.3.0",
    "date": "2026-10-08",
    "title": "Optional Google sign-in",
    "changes": [
      "Create an account or sign in with Google, with a chosen pool username and no extra password required.",
      "Link or unlink Google in account settings. Your account, Premium, stats, and balance stay with you.",
      "Google signup includes Terms acceptance, a recovery code, and the optional passkey offer. Keep at least one working sign-in method.",
      "Account recovery removes Google links and passkeys; Google loads only when you choose to use it."
    ]
  },
  {
    "version": "1.2.1",
    "date": "2026-10-08",
    "title": "Account username changes",
    "changes": [
      "Change your username from account settings once every 365 days. Your first change is available immediately.",
      "Check name availability before saving. Names are case-insensitive and your old name becomes available immediately.",
      "Account identity, admin access, Premium, stats, passkeys and balances stay with your account."
    ]
  },
  {
    "version": "1.2.0",
    "date": "2026-10-08",
    "title": "Optional passkey sign-in",
    "changes": [
      "New accounts can optionally add a passkey after saving their recovery code. Password sign-in remains available.",
      "Sign in with a passkey, including username autofill in supported browsers.",
      "Manage multiple named passkeys in account settings, with creation and last-used dates. Sensitive changes require recent identity verification.",
      "Removing a passkey signs out other sessions. Account recovery removes saved passkeys so you can secure the account again.",
      "Public API documentation and OpenAPI schema endpoints are disabled."
    ]
  },
  {
    "version": "1.1.2",
    "date": "2026-10-08",
    "title": "Install Pool Simulator",
    "changes": [
      "Install app is available in Settings and the welcome screen. Supported browsers open their native install prompt; iPhone and iPad users get Add to Home Screen instructions.",
      "Updated Pool Simulator app metadata, install icons and Apple Home Screen icon.",
      "Camera controls now live in the bottom camera panel. The duplicate top View button is removed; framing, zoom, movement, and return-to-play remain available.",
      "Settings includes a View source link with the official GitHub icon."
    ]
  },
  {
    "version": "1.1.1",
    "date": "2026-10-08",
    "title": "Password visibility and confirmation",
    "changes": [
      "Password fields have accessible eye buttons to show or hide what you type on desktop and mobile.",
      "Creating an account or resetting a password requires a matching second entry. Sign-in still needs only one password.",
      "Passwords return to hidden and are cleared when switching forms, closing the account dialog, or completing a submission."
    ]
  },
  {
    "version": "1.1.0",
    "date": "2026-10-08",
    "title": "Monthly Jev allowances and request accounting",
    "changes": [
      "Daily game limits are replaced by monthly dollar allowances, defaulting to $0.15. Premium remains unlimited; CPU play is free.",
      "The request ledger records TypeSafe-reported tokens and calculates charges precisely. Missing usage is marked unknown with a separate reservation. Jev vs Jev meters both seats individually.",
      "Admin can configure the default and per-account monthly limits, add dollar top-ups for this month, and inspect recent requests. Top-ups are audited and protected against duplicate retries.",
      "An admitted game gets $0.02 completion grace; CPU finishes the rack if that grace runs out.",
      "Admin shows account last-active timestamps alongside creation dates. TypeSafe prepaid balance is explicitly unavailable through its documented API, with a link to billing."
    ]
  },
  {
    "version": "1.0.3",
    "date": "2026-10-08",
    "title": "Simulation games and account controls",
    "changes": [
      "The owner dashboard can disable and re-enable accounts. Disabling revokes existing sessions and blocks sign-in and recovery.",
      "Accounts can be permanently deleted after confirming the username. Private account and Jev records are removed; shared match history is anonymized.",
      "Server-side owner protection prevents disabling or deleting the admin account. Account actions are recorded in the admin audit log.",
      "Accounts granted Sim access can watch CPU vs CPU, Jev vs CPU, or Jev vs Jev on desktop and mobile. Sim access is separate from Premium and starts disabled.",
      "Simulation games share the daily Jev allowance: CPU vs CPU costs zero, Jev vs CPU costs one, and Jev vs Jev costs two. Premium remains unlimited; watching does not change player win/loss stats.",
      "Spin validation tolerates floating-point rounding at the legal boundary. Cue clearance includes spin offsets near rails and pocket facings, consistently in rendering and physics.",
      "A Resume game button recovers interrupted Jev games without starting another rack or consuming another game."
    ]
  },
  {
    "version": "1.0.2",
    "date": "2026-10-08",
    "title": "Fresh opponent games and five free Jev starts",
    "changes": [
      "Selecting Jev starts a fresh rack, just like switching to CPU, on desktop and mobile.",
      "Free accounts can start five Jev games per UTC day; Premium remains unlimited. Resuming a game does not consume a start, and unfinished games do not expire at midnight.",
      "The new allowance starts unused for existing accounts. Previous games and cost history are preserved; shared-network and global Jev usage limits are removed.",
      "The mobile More button is now New Game, and its tray closes after selecting an opponent or New rack."
    ]
  },
  {
    "version": "1.0.1",
    "date": "2026-10-08",
    "title": "Rule-aware opponents and a roomier mobile table",
    "changes": [
      "Desktop Camera now sits directly above the input-mode selector with matching gaps down to the legal links, and expands upward.",
      "Mobile play has a compact combined header and score row, with version and legal links in settings. Portrait uses top/bottom bars and landscape uses side panels. Move Camera swaps the shot tray for camera controls; Done returns to play.",
      "Mobile close zoom preserves the viewing angle near rails instead of forcing an overhead view. An ongoing touch-aim drag can continue beyond the felt edge without taking a shot. Circular touch aiming follows the visible cue-ball center through complete rotations.",
      "New Jev games use your selected Bar, Tournament or Custom rules. Resumed games retain their original rules, and Jev receives the full authoritative rule context.",
      "Premium players can explicitly start a new Jev game with edited rules. Existing games never change rules mid-rack.",
      "CPU and Jev planner power respects custom speed caps; constrained server fallbacks call a legal target.",
      "Shared policy checks cover all 96 categorical custom configurations, with additional actual-shot checks across presets, call rules and speed boundaries."
    ]
  },
  {
    "version": "1.0.0",
    "date": "2026-10-08",
    "title": "Reliable matches and a polished table",
    "changes": [
      "Jev wins and losses now appear in account statistics and survive detailed game retention. Statistics refresh when opening your account.",
      "Terms acceptance stays saved to your account until the agreement text changes. Account controls have clearer checkboxes, spacing and reliable loading states.",
      "Desktop camera controls sit together near the legal links, with centered icons and spin control. Opponent shots preserve your chosen spin.",
      "Calling the 8-ball pocket has a clear status prompt and larger selection targets. The win celebration has refined sparkles and a centered 8-ball emblem.",
      "Mobile HUD uses compact rows to leave more space for the table. Mobile auto-camera frames the cue and relevant shot area more closely; Whole table remains available.",
      "Online shot rejection restores authoritative state so players can retry. Concurrent simulation limits are enforced before asynchronous work begins.",
      "Container builds are checked before release, dependencies use committed lockfiles, and unsuccessful deployment health checks fail CI."
    ]
  },
  {
    "version": "0.9.5",
    "date": "2026-10-08",
    "title": "A gold coin on the table",
    "changes": [
      "The opening toss uses a plain gold coin with H and T faces. It flies in from offscreen, flips, and lands on the felt to reveal who breaks.",
      "Reduced motion shows the selected face without the flight animation. Server-selected breakers and resume behavior remain unchanged.",
      "Close-button X icons are centered consistently across panels and the tutorial."
    ]
  },
  {
    "version": "0.9.4",
    "date": "2026-10-08",
    "title": "Clearer views, account access and winning moments",
    "changes": [
      "After cue-ball placement, automatic camera framing can find a clear legal shot lane and show the cue, target and pocket without changing the shot or called pocket.",
      "Signed-in usernames and account settings are visible from the main HUD. The refined mouse/trackpad selector sits above legal controls, and the app is named Pool Simulator.",
      "Completed racks show the winner, a brief reduced-motion-aware confetti celebration and replay actions for local, CPU, Jev and online sessions.",
      "Fixed an airborne jaw collision and added mirrored flight regressions. Tournament off-table objects stay out; the 8 off on the break is spotted with opponent kitchen placement. Bar keeps its house rules.",
      "The tutorial teaches the actual selected interface: mouse/trackpad pull-and-release with the live power bar, or touch power and Shoot controls.",
      "A coin toss chooses the opening player in local, CPU, Jev and online games. Server games keep their chosen breaker on resume.",
      "Human shots against Jev start moving immediately while the server validates and resolves the shot. Delayed results reconcile after playback without a second strike."
    ]
  },
  {
    "version": "0.9.3",
    "date": "2026-10-08",
    "title": "Welcome flow and guided practice",
    "changes": [
      "New visitors get a welcome screen with game context, explicit terms acceptance, a separate optional analytics choice and a prominent quick tutorial option.",
      "The interactive tutorial uses staged offline practice shots and camera framing, with compact instructions that leave the table and current controls usable on phones and desktops.",
      "Tutorial instructions follow touch, mouse or trackpad controls. Exiting practice restores the previous local game."
    ]
  },
  {
    "version": "0.9.2",
    "date": "2026-10-08",
    "title": "Opponent cue strokes and purposeful cluster shots",
    "changes": [
      "CPU and Jev show their selected aim, cue pullback and stroke before the balls move. The human cue stays hidden while the opponent plans, and player shot controls remain locked during opponent turns. Camera controls remain available.",
      "Opponent strokes respect reduced-motion preferences and cancel safely when leaving or restarting a game.",
      "The AI planner considers firmer shots and measured cluster development, balancing useful openings against scratches and opportunities left to the opponent.",
      "Fixed cue aiming changes during pending shots so the visible direction stays aligned with the submitted shot.",
      "Combined camera input mode, camera actions and contextual hints into a compact HUD panel."
    ]
  },
  {
    "version": "0.9.1",
    "date": "2026-10-08",
    "title": "Trackpad camera controls and keyboard flight",
    "changes": [
      "Added a visible desktop Mouse & Keyboard Mode / Trackpad Mode selector that remembers your preference. Trackpad mode uses two-finger scrolling to orbit, pinch to zoom and Option/Alt-scroll to pan.",
      "Expanded camera flight: WASD moves across the table, Space rises, Left Shift descends and Q/E turns. Enter takes a quick shot only when the table has keyboard focus; Space no longer shoots.",
      "Camera gestures cancel pending shot pulls and protect cue aim while you adjust your view. Visible camera controls provide alternatives to gestures."
    ]
  },
  {
    "version": "0.9.0",
    "date": "2026-10-08",
    "title": "Tactical AI planning and reliable ball placement",
    "changes": [
      "Jev now chooses tactical shots using previews from the real game physics, including direct pots, banks, kicks, combinations and safeties. Plans consider cue position, continuation and opponent replies.",
      "Fixed CPU and Jev ball-in-hand planning so they place the cue and execute a shot together. Head-string shots can plan a legal cushion escape before returning to a ball in the kitchen.",
      "The offline CPU now tests a bounded set of shots before playing, instead of falling back to a break shot when direct pots are unavailable.",
      "Jev records reported token usage even when a model answer is invalid and a local fallback is used. Shot status identifies model choices and local plans."
    ]
  },
  {
    "version": "0.8.0",
    "date": "2026-10-08",
    "title": "Owner dashboard and movable camera controls",
    "changes": [
      "Added a private owner dashboard for searching accounts, managing Premium, and inspecting recorded Jev usage and estimated costs. Owner access is checked on the server and Premium changes are audited.",
      "Settings, View and Online panels can be dragged, moved with buttons or keyboard controls, and reset to their original positions.",
      "Added WASD camera movement and a compact HUD camera pad for touch devices."
    ]
  },
  {
    "version": "0.7.1",
    "date": "2026-10-08",
    "title": "Premium accounts and unlimited Jev games",
    "changes": [
      "Premium accounts have a badge in Settings and Account, with unlimited Jev games and no midnight expiry.",
      "While playing Jev with Premium, New rack starts another server-owned game. Selecting Jev resumes your active game.",
      "Added an operator command to enable or disable Premium. Existing accounts and game records are preserved during the database upgrade."
    ]
  },
  {
    "version": "0.7.0",
    "date": "2026-10-08",
    "title": "Daily Jev AI games and privacy controls",
    "changes": [
      "Added Jev AI alongside the offline CPU. Signed-in adults can start or resume one free Jev game per account and network per UTC day, with server-owned turns and physics.",
      "Added private per-game provider token counts and estimated costs, durable daily game limits, and visible CPU fallback for provider outages or capacity limits.",
      "Added Terms and Privacy notices, explicit adult account/Terms acceptance, and independent opt-in analytics with easy withdrawal and GPC/DNT support.",
      "Optional first-party feature sessions are disabled by default and have no public reporting API. Scheduled retention removes old session events and detailed Jev game records.",
      "Jev credentials remain backend-only. Daily-game revisions reject replayed turns, and the production proxy now replaces untrusted client-IP forwarding headers.",
      "Removed the floating mobile Show balls button; tap either compact scorecard to expand or collapse ball details.",
      "Added an optional interactive Help tutorial for aiming, spin, camera movement and shooting, with action feedback, skippable steps and easy dismissal. The tutorial is centered between scorecards and shot controls."
    ]
  },
  {
    "version": "0.6.2",
    "date": "2026-10-08",
    "title": "CPU opponent terminology",
    "changes": [
      "Standardized the computer opponent name as CPU throughout controls, player cards, turn and result messages, account notes, and documentation. Opponent behavior is unchanged."
    ]
  },
  {
    "version": "0.6.1",
    "date": "2026-10-08",
    "title": "Safer online play",
    "changes": [
      "Removed legacy score and replay endpoints, their unused database models, and the obsolete frontend REST transport. Unknown API reads now return 404 instead of a successful SPA fallback.",
      "Added strict WebSocket field validation, message throttles, connection caps, bounded simulation concurrency, and HTTP request-size limits.",
      "Leaving or losing connection after the first shot counts as a casual forfeit. Production shutdowns and server failures remain interruptions without a fabricated winner.",
      "Account stats explicitly describe unranked casual play; private and custom matches do not establish a competitive rating.",
      "Added browser security headers and HTTPS-only HSTS responses. Production WebSockets now enforce smaller transport queues and message limits.",
      "Mobile spin adjustment and Reset stay visible beside the shot controls; More only collapses secondary actions such as CPU and New rack."
    ]
  },
  {
    "version": "0.6.0",
    "date": "2026-10-08",
    "title": "Room to play on mobile",
    "changes": [
      "Touch and drag to aim continuously, then choose power and tap Shoot. Lifting your aiming finger no longer fires accidentally.",
      "Mobile keeps compact player scores visible, with ball details available on demand and secondary shot controls under More.",
      "Use two fingers together to orbit during play and pinch to zoom in the same gesture. Camera mode still offers one-finger orbit and two-finger panning. Trackpads can use Shift-scroll to orbit.",
      "Placement outlines, cue placement rings, head strings, and pocket-call markers now use a contrasting tint of the chosen felt, with dark guides on light cloth.",
      "Dependabot groups frontend and backend runtime and development updates separately, plus Docker images and GitHub Actions, targeting develop for testing before release."
    ]
  },
  {
    "version": "0.5.0",
    "date": "2026-10-08",
    "title": "Play together, with or without an account",
    "changes": [
      "Create a private room and share its invite link. Friends can join as guests with no account required.",
      "Optional accounts have case-insensitive usernames, secure password hashing, cookie sessions, and a one-use recovery code shown when created. Password recovery issues a new code and revokes existing sessions.",
      "Your account shows lifetime online stats and recent matches recorded by the server, with durable match history for future lobby and matchmaking features. Local practice and CPU games remain untracked.",
      "Online results no longer wait for client acknowledgement. Account identities are verified by the server, and disconnected rooms close cleanly without awarding fabricated wins.",
      "Accounts, sessions, and match history persist in the existing SQLite Docker volume. Added authentication limits, request-origin checks, and protection against serving files outside the frontend directory."
    ]
  },
  {
    "version": "0.4.0",
    "date": "2026-10-08",
    "title": "A natural stance and clearer Bar play",
    "changes": [
      "Settings now has a fixed title bar and close button that stay visible while scrolling, with Escape dismissal and keyboard focus restored to the Table button.",
      "Automatic framing uses a lower, closer playing view and tighter bounds around the visible balls. Cue alignment remains adjustable and never overrides a shot being pulled.",
      "Automatic camera framing now swings behind the cue toward the majority of the current player\u2019s legal targets, including the final 8-Ball, with the idle cue facing forward from the new view.",
      "Bar house rules now keep the layout after an empty break instead of automatically reracking. Players alternate until a legal pot assigns groups; pots made with a scratch stay down without assigning a group.",
      "Turn, foul, and result messages use the CPU opponent\u2019s name consistently. Illegal-break reracks now explain the missing pocket and four-rail requirement.",
      "Once groups are assigned, player cards show the 8-Ball after their group, highlighted when it becomes the final target.",
      "Desktop tables open from behind the cue, looking toward the fresh rack.",
      "Pocketed balls visibly fall into deeper pocket wells before entering the return, while scoring updates immediately.",
      "Release history now includes earlier versions reconstructed from repository history."
    ]
  },
  {
    "version": "0.3.0",
    "date": "2026-10-08",
    "title": "A better view of the table",
    "changes": [
      "New camera controls: enter Move camera mode to orbit with one finger and pinch or pan with two. Focus balls, Whole table, and zoom buttons make adjusting the view easier.",
      "Optional automatic framing follows the remaining balls after a shot settles. Enabled by default on mobile, disabled on desktop; manual camera movement takes priority.",
      "Click the version badge to read this release history.",
      "Warmer lighting, shadows, detailed felt and wood, improved cushions and pockets, table legs, and a visible ball return.",
      "Improved cue geometry and assisted elevation, collision handling, and rolling behavior. Optional fast-forward for fine movements.",
      "Clearer player cards and adaptive instructions, immediate pocket indicators, configurable rail sights and cue ball markings, and a spin reset button.",
      "Bar 8-Ball placement guidance and persistent tutorial dismissal. Browser tests run separately from required deployment checks."
    ]
  },
  {
    "version": "0.2.0",
    "date": "2026-10-07",
    "title": "Playable 8-Ball, online rooms, and a CPU opponent",
    "changes": [
      "Added a playable 3D pool table with racked balls, aiming, shot power, spin, cue ball placement, and 8-Ball rules for turns, fouls, and winning.",
      "Added online rooms with join codes and synchronized shots, plus an optional CPU opponent. Fixed the CPU getting stuck before taking its turn.",
      "Added player scorecards, a compact lobby, felt and wood color settings, and desktop camera orbit with right-drag.",
      "Refined pull-back shot controls, softened the power curve for gentler shots, previewed legal cue ball placement, and made aiming independent of frame rate.",
      "Improved pocket capture and jaw geometry, cushion rebounds, spin and collision behavior, and visible ball rolling. Break shots can use more power than regular shots.",
      "Added game sound effects and an installable web app with offline asset caching."
    ]
  },
  {
    "version": "0.1.1",
    "date": "2026-10-06",
    "title": "Correct version display in deployed builds",
    "changes": [
      "Fixed the version badge by including the version information in deployed builds and correcting where the server reads it."
    ]
  },
  {
    "version": "0.1.0",
    "date": "2026-10-06",
    "title": "Initial application foundation",
    "changes": [
      "Established the browser canvas and backend foundation for the pool simulator; playable pool arrived in a later release.",
      "Added the site version badge and the initial deployment setup."
    ]
  }
]
